import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Star,
  Quote,
  Plus,
  Edit3,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";

interface Testimonial {
  id: string;
  name: string;
  review: string;
  rating: number;
  role?: string;
}

interface TestimonialSectionProps {
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  isEditing: boolean;
  testimonials: Testimonial[];
  onTestimonialsChange: (testimonials: Testimonial[]) => void;
}

export function TestimonialSection({
  colors,
  isEditing,
  testimonials,
  onTestimonialsChange,
}: TestimonialSectionProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Testimonial | null>(null);
  const [newTestimonial, setNewTestimonial] = useState({
    name: "",
    review: "",
    rating: 5,
    role: "",
  });
  const { t } = useTranslation();

  // Auto-play functionality
  useEffect(() => {
    if (testimonials.length <= 1 || isEditing) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length, isEditing]);

  const handleAddTestimonial = () => {
    if (!newTestimonial.name.trim() || !newTestimonial.review.trim()) return;

    const testimonial: Testimonial = {
      id: Date.now().toString(),
      ...newTestimonial,
    };

    onTestimonialsChange([...testimonials, testimonial]);
    setNewTestimonial({ name: "", review: "", rating: 5, role: "" });
    setIsAddingNew(false);
  };

  const handleStartEdit = (testimonial: Testimonial) => {
    setEditingId(testimonial.id);
    setEditData({ ...testimonial });
  };

  const handleSaveEdit = () => {
    if (!editData || !editData.name.trim() || !editData.review.trim()) return;

    const updated = testimonials.map((t) =>
      t.id === editingId ? editData : t
    );
    onTestimonialsChange(updated);
    setEditingId(null);
    setEditData(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData(null);
  };

  const handleDeleteTestimonial = (id: string) => {
    onTestimonialsChange(testimonials.filter((t) => t.id !== id));
    // Reset current index if needed
    if (currentIndex >= testimonials.length - 1) {
      setCurrentIndex(0);
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  const renderStars = (
    rating: number,
    isEditable = false,
    onChange?: (rating: number) => void
  ) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${i < rating ? "fill-current" : "fill-none"} ${
          isEditable
            ? "cursor-pointer hover:fill-current transition-colors"
            : ""
        }`}
        style={{ color: colors.accent }}
        onClick={isEditable && onChange ? () => onChange(i + 1) : undefined}
      />
    ));
  };

  if (testimonials.length === 0 && !isEditing) {
    return null;
  }

  return (
    <section
      className="py-20 relative overflow-hidden"
      style={{ backgroundColor: "#f8fafc" }}
    >
      <div className="container mx-auto px-4 relative max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            {t("testimonials.whatOurCustomersSay")}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t("testimonials.dontJustTakeOurWordForIt")}
          </p>
        </motion.div>

        {/* Testimonial Carousel */}
        {testimonials.length > 0 && (
          <div className="relative max-w-4xl mx-auto">
            <div className="overflow-hidden rounded-3xl">
              <motion.div
                className="flex transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(-${currentIndex * 100}%)`,
                }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={testimonial.id} className="w-full flex-shrink-0">
                    <div className="bg-white p-8 md:p-12 shadow-sm border border-gray-100 mx-4">
                      <div className="text-center max-w-3xl mx-auto">
                        <Quote
                          className="w-12 h-12 mx-auto mb-6 opacity-20"
                          style={{ color: colors.primary }}
                        />

                        {/* Edit Mode for Current Testimonial */}
                        {editingId === testimonial.id && editData ? (
                          <div className="space-y-6">
                            <Textarea
                              value={editData.review}
                              onChange={(e) =>
                                setEditData({
                                  ...editData,
                                  review: e.target.value,
                                })
                              }
                              className="text-center text-lg md:text-xl min-h-[120px] border-gray-200 focus:border-blue-500"
                              placeholder="Customer review..."
                            />
                            <div className="flex justify-center mb-4">
                              {renderStars(editData.rating, true, (rating) =>
                                setEditData({ ...editData, rating })
                              )}
                            </div>
                            <div className="space-y-3">
                              <Input
                                value={editData.name}
                                onChange={(e) =>
                                  setEditData({
                                    ...editData,
                                    name: e.target.value,
                                  })
                                }
                                className="text-center text-lg font-semibold border-gray-200 focus:border-blue-500"
                                placeholder="Customer Name"
                              />
                              <Input
                                value={editData.role || ""}
                                onChange={(e) =>
                                  setEditData({
                                    ...editData,
                                    role: e.target.value,
                                  })
                                }
                                className="text-center text-sm border-gray-200 focus:border-blue-500"
                                placeholder="Role (optional)"
                              />
                            </div>
                            <div className="flex justify-center gap-3 mt-6">
                              <Button
                                onClick={handleSaveEdit}
                                size="sm"
                                style={{ backgroundColor: colors.primary }}
                                className="text-white"
                              >
                                <Check className="w-4 h-4 mr-2" />
                                Save Changes
                              </Button>
                              <Button
                                onClick={handleCancelEdit}
                                variant="outline"
                                size="sm"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed">
                              "{testimonial.review}"
                            </p>
                            <div className="flex justify-center mb-4">
                              {renderStars(testimonial.rating)}
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 text-lg">
                                {testimonial.name}
                              </h4>
                              {testimonial.role && (
                                <p className="text-gray-500 text-sm mt-1">
                                  {testimonial.role}
                                </p>
                              )}
                            </div>

                            {isEditing && (
                              <div className="flex justify-center gap-2 mt-4">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStartEdit(testimonial)}
                                  className="text-gray-500 hover:text-gray-700"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleDeleteTestimonial(testimonial.id)
                                  }
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Navigation Arrows */}
            {testimonials.length > 1 && !isEditing && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={prevSlide}
                  className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow-lg"
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={nextSlide}
                  className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow-lg"
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </>
            )}

            {/* Dots Indicator */}
            {testimonials.length > 1 && (
              <div className="flex justify-center mt-8 gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentIndex
                        ? "scale-125"
                        : "opacity-50 hover:opacity-75"
                    }`}
                    style={{ backgroundColor: colors.primary }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add New Testimonial */}
        {isEditing && (
          <div className="max-w-2xl mx-auto mt-12">
            {!isAddingNew ? (
              <Button
                onClick={() => setIsAddingNew(true)}
                className="w-full"
                style={{ backgroundColor: colors.primary }}
              >
                <Plus className="w-5 h-5 mr-2" />
                Add New Testimonial
              </Button>
            ) : (
              <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">
                  Add New Testimonial
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      placeholder="Customer Name"
                      value={newTestimonial.name}
                      onChange={(e) =>
                        setNewTestimonial({
                          ...newTestimonial,
                          name: e.target.value,
                        })
                      }
                    />
                    <Input
                      placeholder="Role (optional)"
                      value={newTestimonial.role}
                      onChange={(e) =>
                        setNewTestimonial({
                          ...newTestimonial,
                          role: e.target.value,
                        })
                      }
                    />
                  </div>
                  <Textarea
                    placeholder="Customer review..."
                    value={newTestimonial.review}
                    onChange={(e) =>
                      setNewTestimonial({
                        ...newTestimonial,
                        review: e.target.value,
                      })
                    }
                    className="min-h-[100px]"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Rating:</span>
                    <div className="flex gap-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <button
                          key={i}
                          onClick={() =>
                            setNewTestimonial({
                              ...newTestimonial,
                              rating: i + 1,
                            })
                          }
                        >
                          <Star
                            className={`w-5 h-5 ${
                              i < newTestimonial.rating
                                ? "fill-current"
                                : "fill-none"
                            } hover:fill-current transition-colors`}
                            style={{ color: colors.accent }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleAddTestimonial}
                      style={{ backgroundColor: colors.primary }}
                      className="text-white"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Add Testimonial
                    </Button>
                    <Button
                      onClick={() => {
                        setIsAddingNew(false);
                        setNewTestimonial({
                          name: "",
                          review: "",
                          rating: 5,
                          role: "",
                        });
                      }}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
